'use client';

import * as React from 'react';
import {
  animate,
  AnimatePresence,
  motion,
  useInView,
  useMotionValue,
  type MotionValue
} from 'motion/react';

import { AUDIENCE_RESEARCH_NAME } from '@workspace/common/app';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@workspace/ui/components/tabs';

import {
  AccountBuyingGroupScene,
  AccountResearchScene
} from './account-intelligence-scenes';
import { AudienceResearchCanvas } from './audience-research-scene';
import {
  OPERATIONAL_PREVIEW_COPY,
  OperationalIntelligenceCanvas
} from './operational-intelligence-previews';

type IntelligencePreviewProps = {
  previewNumber: string;
  reduced: boolean;
  compactHeader?: boolean;
};

type PreviewLayout = 'account' | 'audience';

type VisualKind =
  | 'trend'
  | 'bars'
  | 'timeline'
  | 'people'
  | 'funnel'
  | 'actions';

type StoryRow = {
  label: string;
  detail: string;
  value: string;
  score?: number;
};

type Story = {
  id: string;
  label: string;
  kicker: string;
  headline: string;
  metric: string;
  metricLabel: string;
  kind: VisualKind;
  rows: readonly StoryRow[];
  chart?: readonly { label: string; value: number }[];
  interpretation: string;
  decision: string;
  nextMove: string;
  sources: readonly string[];
};

type PreviewMode = {
  eyebrow: string;
  title: string;
  status: 'Illustrative product view';
  layout: PreviewLayout;
  storyIds: readonly string[];
  stories: readonly Story[];
};

const ACCOUNT_SEQUENCE_MS = 6500;
const ACCOUNT_QUESTION_MS = 4500;

const MODES: Record<string, PreviewMode> = {
  '02': {
    eyebrow: 'Account intelligence',
    title: 'Understand the account and your next move.',
    status: 'Illustrative product view',
    layout: 'account',
    storyIds: ['question', 'brief', 'committee', 'activation'],
    stories: [
      {
        id: 'question',
        label: 'your question',
        kicker: 'Quillstack · research request',
        headline: 'Should we reach out to this account?',
        metric: '1',
        metricLabel: 'account to understand',
        kind: 'actions',
        rows: [],
        interpretation:
          'Start with the account and the decision you need to make.',
        decision: 'Research Quillstack before deciding how to engage.',
        nextMove:
          'Review what changed, the relevant people, and the next move.',
        sources: []
      },
      {
        id: 'brief',
        label: 'account brief',
        kicker: 'Quillstack · account brief',
        headline: 'The account moved from warm to active this week.',
        metric: '3',
        metricLabel: 'corroborating signals',
        kind: 'timeline',
        rows: [
          {
            label: 'New commercial leader',
            detail: 'Nadia Brooks joined as Chief Commercial Officer.',
            value: 'Today'
          },
          {
            label: 'Enterprise packaging changed',
            detail: 'Usage controls now lead the pricing page.',
            value: '2d'
          },
          {
            label: 'Evaluation reopened',
            detail: 'The opportunity moved from closed-lost to evaluation.',
            value: '6d'
          }
        ],
        interpretation:
          'This is not a generic intent spike. Leadership, product packaging, and prior deal context all point to the same new buying window.',
        decision: 'Move Quillstack into the active-account campaign this week.',
        nextMove:
          'Brief sales on the new mandate and lead with governance proof—not a generic product overview.',
        sources: ['Public evidence', 'CRM history', 'People movement']
      },
      {
        id: 'committee',
        label: 'buying group',
        kicker: 'Quillstack · stakeholder map',
        headline: 'Three people shape the buying decision.',
        metric: '3',
        metricLabel: 'key roles mapped',
        kind: 'people',
        rows: [
          {
            label: 'Maya Chen',
            detail: 'Potential executive sponsor · company direction',
            value: 'CEO'
          },
          {
            label: 'Nadia Brooks',
            detail: 'Operational owner · new systems mandate',
            value: 'CCO'
          },
          {
            label: 'Theo Shah',
            detail: 'Likely champion · prior evaluator',
            value: 'VP RevOps'
          }
        ],
        interpretation:
          'Marketing can see who must believe the story, what each person likely cares about, and where executive coverage is still missing.',
        decision: 'Build the account narrative for a committee, not one lead.',
        nextMove:
          'Give the CEO an outcome story, the CCO proof for the new mandate, and the prior evaluator a rollout guide.',
        sources: ['Role history', 'Public activity', 'CRM contacts']
      },
      {
        id: 'history',
        label: 'campaign history',
        kicker: 'Quillstack · message history',
        headline: 'The old message stalled. The new mandate tells you why.',
        metric: '41d',
        metricLabel: 'since last touch',
        kind: 'bars',
        rows: [
          {
            label: 'Speed-led message',
            detail: 'Opened, no reply',
            value: '12%',
            score: 24
          },
          {
            label: 'Cost-led message',
            detail: 'Low committee engagement',
            value: '19%',
            score: 38
          },
          {
            label: 'Governance proof',
            detail: 'Matches the new mandate',
            value: 'High fit',
            score: 86
          }
        ],
        interpretation:
          'The previous campaign was not simply mistimed; it centered benefits that no longer match the account’s emerging priority.',
        decision: 'Retire the speed-first creative for this account.',
        nextMove:
          'Re-enter with a control-and-review narrative tied to the pricing change and Nadia’s remit.',
        sources: ['Campaign response', 'Account research', 'Message themes']
      },
      {
        id: 'activation',
        label: 'activation plan',
        kicker: 'Quillstack · recommended outreach',
        headline: 'Re-engage the prior evaluator and bring in the new leader.',
        metric: '1',
        metricLabel: 'next action',
        kind: 'actions',
        rows: [
          {
            label: 'Re-engage Theo, then bring in Nadia.',
            detail:
              'Connect the reopened evaluation to the new commercial mandate.',
            value: 'Account owner'
          }
        ],
        interpretation:
          'The value is not another account score. It is a shared, evidence-backed plan that marketing and sales can execute together.',
        decision: 'Re-engage Theo, then bring in Nadia.',
        nextMove:
          'Lead with the reopened evaluation. Show how usage controls support her new commercial mandate.',
        sources: [
          'Reopened evaluation',
          'Prior evaluator',
          'New commercial leader'
        ]
      }
    ]
  }
};

export function IntelligenceExplorerPreview({
  previewNumber,
  reduced,
  compactHeader = false
}: IntelligencePreviewProps): React.JSX.Element {
  if (previewNumber === '03') {
    return (
      <PreviewSceneFrame
        copy={{
          eyebrow: AUDIENCE_RESEARCH_NAME,
          title: 'Understand what your audience cares about.'
        }}
      >
        <AudienceResearchCanvas reduced={reduced} />
      </PreviewSceneFrame>
    );
  }

  if (
    previewNumber === '04' ||
    previewNumber === '05' ||
    previewNumber === '06'
  ) {
    return (
      <PreviewSceneFrame copy={OPERATIONAL_PREVIEW_COPY[previewNumber]}>
        <OperationalIntelligenceCanvas
          key={previewNumber}
          previewNumber={previewNumber}
          reduced={reduced}
        />
      </PreviewSceneFrame>
    );
  }

  const mode = MODES[previewNumber] ?? MODES['02']!;

  return (
    <ExecutivePreview
      key={previewNumber}
      mode={mode}
      reduced={reduced}
      compactHeader={compactHeader}
    />
  );
}

function PreviewSceneFrame({
  copy,
  children
}: {
  copy: Pick<PreviewMode, 'eyebrow' | 'title'>;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <PreviewHeader mode={copy} />
      <div className="pt-5 md:pt-6">
        <div className="overflow-hidden rounded-[22px] border border-[color:var(--hairline)] bg-[color:var(--paper)] shadow-[0_20px_55px_rgba(32,26,20,0.065)]">
          {children}
        </div>
      </div>
    </div>
  );
}

function ExecutivePreview({
  mode,
  reduced,
  compactHeader = false
}: {
  mode: PreviewMode;
  reduced: boolean;
  compactHeader?: boolean;
}): React.JSX.Element {
  const stories = React.useMemo(
    () =>
      mode.storyIds
        .map((storyId) => mode.stories.find((story) => story.id === storyId))
        .filter((story): story is Story => Boolean(story)),
    [mode]
  );
  const [activeStory, setActiveStory] = React.useState(stories[0]!.id);
  const [direction, setDirection] = React.useState(1);
  const [resolved, setResolved] = React.useState(reduced);
  const [cycle, setCycle] = React.useState(0);
  const [hovered, setHovered] = React.useState(false);
  const [keyboardFocus, setKeyboardFocus] = React.useState(false);
  const [manuallyPaused, setManuallyPaused] = React.useState(false);
  const [pageVisible, setPageVisible] = React.useState(true);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(previewRef, { amount: 0.4 });
  const progress = useMotionValue(0);
  const paused =
    (hovered && activeStory !== 'question') ||
    keyboardFocus ||
    manuallyPaused ||
    !pageVisible ||
    !inView;
  const hasViewSelector = stories.length > 1;

  React.useEffect(() => {
    const updateVisibility = (): void => setPageVisible(!document.hidden);
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    return () =>
      document.removeEventListener('visibilitychange', updateVisibility);
  }, []);

  React.useEffect(() => {
    if (reduced) {
      setResolved(true);
      return;
    }

    setResolved(false);
    const timer = window.setTimeout(() => setResolved(true), 720);

    return () => window.clearTimeout(timer);
  }, [activeStory, reduced]);

  const selectStory = React.useCallback(
    (nextStory: string) => {
      const currentIndex = stories.findIndex(
        (story) => story.id === activeStory
      );
      const nextIndex = stories.findIndex((story) => story.id === nextStory);
      setResolved(reduced);
      setDirection(nextIndex >= currentIndex ? 1 : -1);
      progress.set(0);
      setCycle((current) => (nextStory === 'question' ? current + 1 : current));
      setActiveStory(nextStory);
    },
    [activeStory, progress, reduced, stories]
  );

  React.useEffect(() => {
    if (mode.layout !== 'account' || reduced || paused || stories.length < 2)
      return;

    // The ring and automatic navigation share a clock, including every pause.
    const playback = animate(progress, 1, {
      duration:
        ((1 - progress.get()) *
          (activeStory === 'question'
            ? ACCOUNT_QUESTION_MS
            : ACCOUNT_SEQUENCE_MS)) /
        1000,
      ease: 'linear',
      onComplete: () => {
        const currentIndex = stories.findIndex(
          (story) => story.id === activeStory
        );
        const nextStory = stories[(currentIndex + 1) % stories.length];
        if (!nextStory) return;
        progress.set(0);
        setResolved(false);
        setDirection(1);
        setActiveStory(nextStory.id);
      }
    });

    return () => playback.stop();
  }, [activeStory, cycle, mode.layout, paused, progress, reduced, stories]);

  return (
    <div
      ref={previewRef}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
      onFocusCapture={(event) => {
        if (event.target.matches(':focus-visible')) setKeyboardFocus(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setKeyboardFocus(false);
      }}
    >
      <PreviewHeader
        mode={mode}
        compact={compactHeader}
        controls={
          mode.layout === 'account' && !reduced ? (
            <button
              type="button"
              aria-label={
                manuallyPaused
                  ? 'Play account preview'
                  : 'Pause account preview'
              }
              onClick={() => {
                setManuallyPaused((current) => !current);
                setKeyboardFocus(false);
              }}
              className="-my-3 grid size-10 place-items-center rounded-full text-[color:var(--ink-4)] transition-colors hover:bg-[color:var(--mint)] hover:text-[color:var(--accent-darker)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600"
            >
              <svg
                aria-hidden
                viewBox="0 0 16 16"
                className="size-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {manuallyPaused ? (
                  <path d="m5 3 7 5-7 5V3Z" />
                ) : (
                  <path d="M5.5 3.5v9M10.5 3.5v9" />
                )}
              </svg>
            </button>
          ) : undefined
        }
      />
      <Tabs
        value={activeStory}
        onValueChange={selectStory}
        className={
          mode.layout === 'account' ? 'gap-0 pt-4' : 'gap-0 pt-5 md:pt-6'
        }
      >
        <div className="overflow-hidden rounded-[22px] border border-[color:var(--hairline)] bg-[color:var(--paper)] shadow-[0_20px_55px_rgba(32,26,20,0.065)]">
          {hasViewSelector && (
            <div className="relative min-w-0 border-b border-[color:var(--hairline)] bg-[color:var(--paper-2)]">
              {mode.layout === 'account' ? (
                <AccountStoryRail
                  stories={stories}
                  activeStory={activeStory}
                  reduced={reduced}
                  progress={progress}
                />
              ) : (
                <TabsList
                  aria-label={`${mode.eyebrow} views`}
                  className="flex h-[68px] w-full justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {stories.map((story, index) => (
                    <TabsTrigger
                      key={story.id}
                      value={story.id}
                      className="group relative h-full min-w-[156px] flex-none justify-start overflow-hidden rounded-[11px] border-0 bg-transparent px-3 py-2.5 text-left shadow-none transition-colors hover:text-[color:var(--ink)] focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-teal-500 data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--ink)] data-[state=active]:shadow-none md:min-w-[210px] md:justify-center"
                    >
                      {story.id === activeStory && (
                        <motion.span
                          layoutId={`${mode.eyebrow}-story-highlight`}
                          aria-hidden
                          className="absolute inset-0 rounded-[11px] border border-[color:var(--hairline)] bg-[color:var(--paper)] shadow-[0_8px_22px_rgba(32,26,20,0.055)]"
                          transition={
                            reduced
                              ? { duration: 0.01 }
                              : {
                                  type: 'spring',
                                  stiffness: 420,
                                  damping: 34,
                                  mass: 0.72
                                }
                          }
                        />
                      )}
                      <span className="relative z-10 flex min-w-0 items-center gap-2">
                        <span className="font-mono text-[8.5px] tabular-nums tracking-[0.12em] text-[color:var(--ink-5)]">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="block truncate text-[11.5px] font-medium leading-tight text-[color:var(--ink-4)] group-data-[state=active]:font-semibold group-data-[state=active]:text-[color:var(--accent-darker)]">
                          {story.label}
                        </span>
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              )}
              {mode.layout !== 'account' && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[color:var(--paper-2)] to-transparent md:hidden"
                />
              )}
            </div>
          )}

          <div
            className={
              hasViewSelector
                ? 'relative h-[568px] min-w-0 sm:h-[548px] md:h-[388px]'
                : 'relative h-[636px] min-w-0 sm:h-[616px] md:h-[456px]'
            }
          >
            <AnimatePresence mode="sync">
              {(mode.layout === 'account'
                ? stories.filter((story) => story.id === activeStory)
                : stories
              ).map((story) => (
                <TabsContent
                  key={
                    mode.layout === 'account'
                      ? `${story.id === 'question' || story.id === 'brief' ? 'research' : story.id}-${cycle}`
                      : story.id
                  }
                  value={story.id}
                  forceMount={mode.layout === 'account' ? true : undefined}
                  className={
                    mode.layout === 'account'
                      ? 'absolute inset-0 mt-0 h-full'
                      : 'absolute inset-0 mt-0 h-full data-[state=inactive]:hidden'
                  }
                >
                  <DecisionCanvas
                    story={story}
                    resolved={resolved}
                    reduced={reduced}
                    direction={direction}
                    layout={mode.layout}
                    progress={progress}
                    accountRows={
                      stories.find((item) => item.id === 'brief')!.rows
                    }
                  />
                </TabsContent>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </Tabs>
    </div>
  );
}

function AccountStoryRail({
  stories,
  activeStory,
  reduced,
  progress
}: {
  stories: readonly Story[];
  activeStory: string;
  reduced: boolean;
  progress: MotionValue<number>;
}): React.JSX.Element {
  return (
    <TabsList
      aria-label="Account intelligence views"
      className="relative flex h-[76px] w-full justify-stretch overflow-visible rounded-none bg-transparent p-0"
    >
      <span
        aria-hidden
        style={{
          left: `${50 / stories.length}%`,
          right: `${50 / stories.length}%`
        }}
        className="absolute top-[27px] h-px bg-[color:var(--hairline)]"
      />
      {stories.map((story, index) => {
        const selected = story.id === activeStory;

        return (
          <TabsTrigger
            key={story.id}
            value={story.id}
            aria-label={`${String(index).padStart(2, '0')} ${story.label}`}
            className="group relative z-10 h-full min-w-0 flex-1 flex-col justify-start gap-1.5 overflow-visible rounded-none border-0 bg-transparent px-1 pt-3 shadow-none focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none sm:px-3"
          >
            <span className="relative grid size-8 shrink-0 place-items-center rounded-full bg-[color:var(--paper-2)] font-mono text-[10px] font-medium tabular-nums text-[color:var(--ink-4)] transition-colors group-data-[state=active]:text-white">
              {selected && (
                <motion.span
                  layoutId="account-story-stop"
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-[color:var(--accent-darker)]"
                  transition={
                    reduced
                      ? { duration: 0.01 }
                      : {
                          type: 'spring',
                          stiffness: 360,
                          damping: 28,
                          mass: 0.75
                        }
                  }
                />
              )}
              <span className="relative z-10">
                {String(index).padStart(2, '0')}
              </span>
              {selected && !reduced && (
                <svg
                  aria-hidden
                  viewBox="0 0 40 40"
                  className="absolute -inset-1 size-10 -rotate-90 overflow-visible"
                >
                  <circle
                    cx="20"
                    cy="20"
                    r="19"
                    fill="none"
                    stroke="var(--accent-deep)"
                    strokeOpacity="0.12"
                    strokeWidth="1.5"
                  />
                  <motion.circle
                    cx="20"
                    cy="20"
                    r="19"
                    fill="none"
                    stroke="var(--accent-deep)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    style={{ pathLength: progress }}
                  />
                </svg>
              )}
            </span>
            <span className="max-w-full truncate text-[10px] font-medium leading-none text-[color:var(--ink-4)] transition-colors group-data-[state=active]:font-semibold group-data-[state=active]:text-[color:var(--accent-darker)] sm:text-[11.5px]">
              <span className="sm:hidden">
                {['question', 'brief', 'people', 'plan'][index]}
              </span>
              <span className="hidden sm:inline">{story.label}</span>
            </span>
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}

function PreviewHeader({
  mode,
  controls,
  compact = false
}: {
  mode: Pick<PreviewMode, 'eyebrow' | 'title'>;
  controls?: React.ReactNode;
  compact?: boolean;
}): React.JSX.Element {
  if (compact) {
    return (
      <div className="flex min-h-9 items-center justify-between gap-3 px-1 text-xs text-[color:var(--ink-4)]">
        <span>Illustrative example</span>
        {controls}
      </div>
    );
  }
  return (
    <header className="min-h-[108px] border-b border-[color:var(--hairline-2)] pb-6 md:min-h-0">
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-[color:var(--ink-4)]">
          {mode.eyebrow}
        </p>
        {controls}
      </div>
      <h2 className="display-h2 mt-3 text-[22px] leading-[1.08] text-[color:var(--ink)] md:text-[24px] lg:text-[26px]">
        {mode.title}
      </h2>
    </header>
  );
}

function DecisionCanvas({
  story,
  resolved,
  reduced,
  direction,
  layout,
  progress,
  accountRows
}: {
  story: Story;
  resolved: boolean;
  reduced: boolean;
  direction: number;
  layout: PreviewLayout;
  progress: MotionValue<number>;
  accountRows: readonly StoryRow[];
}): React.JSX.Element {
  return (
    <motion.article
      key={
        story.id === 'question' || story.id === 'brief' ? 'research' : story.id
      }
      initial={
        reduced
          ? false
          : {
              opacity: 0,
              x: layout === 'account' ? 0 : direction * 18,
              filter: layout === 'account' ? 'blur(0px)' : 'blur(3px)'
            }
      }
      animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
      exit={
        reduced
          ? undefined
          : {
              opacity: 0,
              x: layout === 'account' ? 0 : direction * -14,
              filter: layout === 'account' ? 'blur(0px)' : 'blur(2px)',
              transition: { duration: 0.24 }
            }
      }
      transition={{
        duration: reduced ? 0.01 : layout === 'account' ? 0.45 : 0.3,
        ease: [0.2, 0.72, 0.24, 1]
      }}
      className="h-full min-w-0 overflow-hidden"
    >
      {layout === 'account' && (
        <AccountBriefCanvas
          story={story}
          resolved={resolved}
          reduced={reduced}
          progress={progress}
          accountRows={accountRows}
        />
      )}
    </motion.article>
  );
}

function AccountBriefCanvas({
  story,
  reduced,
  progress,
  accountRows
}: {
  story: Story;
  resolved: boolean;
  reduced: boolean;
  progress: MotionValue<number>;
  accountRows: readonly StoryRow[];
}): React.JSX.Element {
  if (story.id === 'question' || story.id === 'brief') {
    return (
      <AccountResearchScene
        step={story.id}
        rows={accountRows}
        reduced={reduced}
        progress={progress}
      />
    );
  }

  if (story.id === 'committee') {
    return (
      <AccountBuyingGroupScene
        rows={story.rows}
        reduced={reduced}
      />
    );
  }

  return (
    <section className="flex h-full min-h-0 items-center justify-center overflow-hidden px-5 py-6 md:px-10">
      <motion.div
        initial={reduced ? false : { opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 24 }}
        className="w-full max-w-[650px] rounded-[18px] border border-teal-500/20 bg-[color:var(--mint)]/55 p-5 shadow-[0_18px_50px_rgba(0,90,68,0.08)] md:p-7"
      >
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <motion.span
            aria-hidden
            initial={reduced ? false : { scale: 0.6, rotate: -18 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 18,
              delay: reduced ? 0 : 0.2
            }}
            className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-teal-500 text-white"
          >
            <svg
              viewBox="0 0 18 18"
              className="h-4 w-4"
            >
              <path
                d="M3.5 9h10.5M10.5 5.5 14 9l-3.5 3.5"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
          </motion.span>
          <div>
            <p className="text-[19px] font-semibold leading-[1.12] tracking-[-0.025em] text-[color:var(--ink)] md:text-[24px]">
              {story.decision}
            </p>
            <p className="mt-3 max-w-[48ch] text-[12px] leading-relaxed text-[color:var(--ink-4)] md:text-[13px]">
              {story.nextMove}
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
